<template>
    <main class="contact-page position-relative overflow-hidden">
        <img class="contact-page__corner position-absolute top-0 end-0" src="/img/svg/nav-corner-1.svg" alt="">

        <div class="container position-relative py-5">
            <div class="row align-items-center g-4 g-lg-5">
                <div class="col-12 col-lg-5">
                    <div class="contact-page__intro pe-lg-4">
                        <p class="contact-page__eyebrow mb-3">{{$t('marketing.contact.eyebrow')}}</p>
                        <h1 class="display-5 fw-bolder text-gray-900 mb-4">{{$t('marketing.contact.title')}}</h1>
                        <p class="fs-5 text-gray-600 mb-4">{{$t('marketing.contact.subtitle')}}</p>

                        <a class="contact-page__email-card d-flex align-items-center gap-3 text-decoration-none" href="mailto:contact@tapgiant.com">
                            <span class="contact-page__email-icon flex-shrink-0">
                                <BootstrapIcon name="envelope" :size="22" />
                            </span>
                            <span class="min-w-0">
                                <span class="d-block small text-gray-500 mb-1">{{$t('marketing.contact.email_intro')}}</span>
                                <strong class="d-block text-gray-900 text-break">contact[at]tapgiant.com</strong>
                            </span>
                            <BootstrapIcon class="contact-page__email-arrow ms-auto flex-shrink-0" name="arrow-up-right" :size="17" />
                        </a>

                        <div class="contact-page__reassurance mt-4">
                            <div class="d-flex gap-3 mb-3">
                                <BootstrapIcon class="flex-shrink-0 mt-1" color="fill-teal-600 text-teal-600" name="chat-dots" :size="19" />
                                <div>
                                    <h2 class="h6 fw-bolder mb-1">{{$t('marketing.contact.context_title')}}</h2>
                                    <p class="small text-gray-600 mb-0">{{$t('marketing.contact.context_description')}}</p>
                                </div>
                            </div>
                            <div class="d-flex gap-3">
                                <BootstrapIcon class="flex-shrink-0 mt-1" color="fill-teal-600 text-teal-600" name="tools" :size="19" />
                                <div>
                                    <h2 class="h6 fw-bolder mb-1">{{$t('marketing.contact.setup_title')}}</h2>
                                    <p class="small text-gray-600 mb-0">{{$t('marketing.contact.setup_description')}}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-12 col-lg-7">
                    <div class="contact-form-card bg-white">
                        <div class="mb-4">
                            <h2 class="h3 fw-bolder text-gray-900 mb-2">{{$t('marketing.contact.form_title')}}</h2>
                            <p class="text-gray-600 mb-0">{{$t('marketing.contact.form_description')}}</p>
                        </div>

                        <form novalidate @submit.prevent="submit()">
                            <div class="row g-3">
                                <div class="col-12 col-md-6">
                                    <label for="contact-name" class="contact-form-card__label form-label fw-bolder">
                                        {{$t('marketing.contact.name')}}
                                        <span class="fw-normal text-gray-500">{{$t('marketing.contact.optional')}}</span>
                                    </label>
                                    <div class="contact-form-card__control d-flex align-items-center">
                                        <BootstrapIcon class="contact-form-card__control-icon flex-shrink-0" name="person" :size="18" />
                                        <input
                                        id="contact-name"
                                        v-model="form_name"
                                        type="text"
                                        class="form-control border-0 shadow-none"
                                        :placeholder="$t('marketing.contact.name_placeholder')"
                                        autocomplete="name"
                                        >
                                    </div>
                                </div>

                                <div class="col-12 col-md-6">
                                    <div class="d-flex align-items-center justify-content-between gap-2">
                                        <label for="contact-email" class="contact-form-card__label form-label fw-bolder">
                                            {{$t('marketing.contact.email')}}
                                            <span class="fw-normal text-gray-500">{{$t('marketing.contact.optional')}}</span>
                                        </label>
                                        <span v-if="!is_valid_email" class="small text-orange-600">{{$t('marketing.contact.email_invalid')}}</span>
                                    </div>
                                    <div class="contact-form-card__control d-flex align-items-center" :class="{'contact-form-card__control--invalid': !is_valid_email}">
                                        <BootstrapIcon class="contact-form-card__control-icon flex-shrink-0" name="envelope" :size="18" />
                                        <input
                                        id="contact-email"
                                        v-model="form_email"
                                        type="email"
                                        class="form-control border-0 shadow-none"
                                        :placeholder="$t('marketing.contact.email_placeholder')"
                                        :aria-invalid="!is_valid_email"
                                        aria-describedby="contact-email-help"
                                        autocomplete="email"
                                        >
                                    </div>
                                    <small id="contact-email-help" class="d-block text-gray-500 mt-2">{{$t('marketing.contact.email_help')}}</small>
                                </div>

                                <div class="col-12">
                                    <div class="d-flex align-items-center justify-content-between gap-3">
                                        <label for="contact-message" class="contact-form-card__label form-label fw-bolder">{{$t('marketing.contact.message')}}</label>
                                        <span class="contact-form-card__counter small" :class="{'contact-form-card__counter--invalid': form_message.length > 0 && !is_valid_message}">
                                            {{form_message.length}}/500
                                        </span>
                                    </div>
                                    <textarea
                                    id="contact-message"
                                    v-model="form_message"
                                    class="contact-form-card__textarea form-control shadow-none"
                                    rows="6"
                                    maxlength="500"
                                    :placeholder="$t('marketing.contact.message_placeholder')"
                                    aria-describedby="contact-message-help"
                                    ></textarea>
                                    <small id="contact-message-help" class="d-block text-gray-500 mt-2">{{$t('marketing.contact.message_help')}}</small>
                                </div>
                            </div>

                            <TurnstileWidget ref="turnstile" v-model="turnstile_token" class="mt-4" />

                            <div v-if="success" class="contact-form-card__notice contact-form-card__notice--success d-flex gap-2 mt-4" role="status">
                                <BootstrapIcon class="flex-shrink-0 mt-1" name="check-circle-fill" :size="18" />
                                <span>{{$t('marketing.contact.success')}}</span>
                            </div>
                            <div v-if="error" class="contact-form-card__notice contact-form-card__notice--error d-flex gap-2 mt-4" role="alert">
                                <BootstrapIcon class="flex-shrink-0 mt-1" name="exclamation-circle-fill" :size="18" />
                                <span>{{$t('marketing.contact.error')}}</span>
                            </div>

                            <div class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-3 mt-4">
                                <button :disabled="!is_valid_email || !is_valid_message || !turnstile_token || loading" type="submit" class="contact-form-card__submit btn btn-brand btn-lg text-white fw-bolder px-4">
                                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                                    <span>{{loading ? $t('marketing.contact.sending') : $t('marketing.contact.send')}}</span>
                                </button>
                                <p class="contact-form-card__agreement small text-gray-500 mb-0">
                                    {{$t('marketing.contact.agreement_prefix')}}
                                    <router-link :to="{ name: 'privacy' }">{{$t('marketing.contact.privacy_policy')}}</router-link>
                                    {{$t('marketing.contact.and')}}
                                    <router-link :to="{ name: 'terms' }">{{$t('marketing.contact.terms_conditions')}}</router-link>.
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import { REGEX_EMAIL } from '@/common/constants'
import TurnstileWidget from '@/common/components/TurnstileWidget.vue'
import BootstrapIcon from '@/common/directive/BootstrapIcon.vue'

export default {
    components: {
        BootstrapIcon,
        TurnstileWidget
    },
    data: function () {
        return {
            form_name: '',
            form_email: '',
            form_message: '',
            turnstile_token: '',
            loading: false,
            error: 0,
            success: false
        }
    },
    computed: {
        is_valid_email: function () {
            return this.form_email.length === 0 || REGEX_EMAIL.test(this.form_email)
        },
        is_valid_message: function () {
            return this.form_message.length >= 10 && this.form_message.length <= 500
        }
    },
    watch: {
        form_email: function () {
            this.error = 0
        },
        form_message: function () {
            this.error = 0
        }
    },
    methods: {
        escape_param: function (toOutput) {
            return toOutput.replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#x27')
                .replace(/\//g, '&#x2F')
        },
        submit: async function () {
            if (!this.is_valid_email || !this.is_valid_message || !this.turnstile_token || this.loading) return

            this.loading = true
            this.error = 0
            this.success = false
            const q = {
                message: this.escape_param(this.form_message),
                'cf-turnstile-response': this.turnstile_token
            }
            if (this.form_name.length > 0) q.name = this.form_name
            if (this.form_email.length > 0) q.email = this.form_email

            try {
                const response = await this.$store.dispatch('CONTACT', q)
                if (response && response.status === 0) {
                    this.form_email = ''
                    this.form_name = ''
                    this.form_message = ''
                    this.success = true
                } else {
                    this.error = response && response.error_code ? response.error_code : 1
                }
            } catch (error) {
                this.error = 1
            } finally {
                this.loading = false
                this.$refs.turnstile?.reset?.()
            }
        }
    }
}
</script>
